/**
 * AEO/GEO 최적화를 위한 JSON-LD 스키마 빌더 유틸리티
 * - AI 검색엔진(ChatGPT, Perplexity, Google AI Overview)이 인용하기 좋은 자연어 description 생성
 * - schema.org 스펙 준수
 */

const FALLBACK_IMAGE = 'https://cdn.daisomall.co.kr/file/CM/daisologo_1.91-1.png'
const BASE_URL = 'https://www.daisomall.co.kr'

/**
 * 상품 자연어 description 생성
 * @param {Object} pd - 상품 데이터
 * @param {string} pd.name - 상품명
 * @param {string} pd.brand - 브랜드명
 * @param {string} pd.category - 카테고리명
 * @param {number|string} pd.price - 가격
 * @param {string} [pd.detailText] - 상세설명 텍스트 (HTML 제거 후)
 * @param {Object} [pd.rating] - 리뷰 { avg, count }
 * @param {string} [pd.availability] - 재고 상태 ('InStock' | 'OutOfStock')
 * @returns {string}
 */
export function buildProductDescription (pd) {
  const { name, brand, category, price, detailText, rating, availability } = pd

  // 상세설명이 있으면 우선 사용 (GEO 인용률 향상)
  if (detailText && detailText.length > 30) {
    // 품질표시/규격 정보 제거 (AI 인용에 불필요)
    let cleaned = detailText
      .replace(/품질표시[\s\S]*$/i, '')
      .replace(/제조국[:\s]*[^\s]*/g, '')
      .replace(/수입원[:\s]*[^판]*판매원[:\s]*[^\s]*/g, '')
      .replace(/판매원[:\s]*[\s\S]*$/i, '')
      .replace(/\s+/g, ' ')
      .trim()

    // 정제 후 너무 짧으면 원본 사용
    if (cleaned.length < 30) { cleaned = detailText }

    let desc = cleaned.substring(0, 300)
    const extras = []
    if (price) {
      const priceStr = typeof price === 'number' ? price.toLocaleString() : Number(price).toLocaleString()
      extras.push(`가격 ${priceStr}원`)
    }
    if (rating && rating.count > 0) {
      extras.push(`리뷰 ${rating.avg}점(${rating.count}건)`)
    }
    if (extras.length > 0) {
      desc += ` [${extras.join(', ')}]`
    }
    return desc
  }

  // 자연어형 description 조합 (폴백)
  const parts = []
  if (name) {
    const brandPart = brand && brand !== '다이소' ? `${brand}의 ` : '다이소 '
    const categoryPart = category ? `${category} 카테고리의 상품` : '상품'
    parts.push(`${brandPart}${name}은(는) ${categoryPart}입니다.`)
  }
  if (price) {
    const priceStr = typeof price === 'number' ? price.toLocaleString() : Number(price).toLocaleString()
    parts.push(`가격은 ${priceStr}원이며, 다이소몰에서 온라인 구매 및 매장픽업이 가능합니다.`)
  }
  if (rating && rating.count > 0) {
    parts.push(`리뷰 평점 ${rating.avg}점(${rating.count}건).`)
  }
  if (availability === 'OutOfStock') {
    parts.push('현재 품절 상품입니다.')
  }
  if (parts.length > 0) {
    parts.push('다이소의 균일가 상품으로 합리적인 가격에 만나보세요.')
  }

  return parts.join(' ') || '다이소몰에서 다양한 상품을 합리적인 가격에 만나보세요.'
}

/**
 * 카테고리 자연어 description 생성
 * @param {Object} params
 * @param {string} params.categoryName - 카테고리명
 * @param {string} [params.categoryPath] - 카테고리 경로 (대 > 중 > 소)
 * @returns {string}
 */
export function buildCategoryDescription ({ categoryName, categoryPath }) {
  if (!categoryName) {
    return '다이소몰에서 다양한 카테고리의 상품을 합리적인 가격에 만나보세요.'
  }
  const pathPart = categoryPath ? ` ${categoryPath} 카테고리의` : ''
  return `다이소몰 ${categoryName} 카테고리에서${pathPart} 다양한 상품을 합리적인 가격에 만나보세요. 생활용품 전문 온라인몰 다이소에서 필요한 상품을 찾아보세요.`
}

/**
 * 검색결과 자연어 description 생성
 * @param {Object} params
 * @param {string} params.searchTerm - 검색어
 * @returns {string}
 */
export function buildSearchDescription ({ searchTerm }) {
  if (!searchTerm) {
    return '다이소몰에서 원하는 상품을 검색해보세요.'
  }
  return `다이소몰에서 ${searchTerm} 검색 결과를 확인하세요. 다이소의 다양한 상품을 합리적인 가격에 만나보실 수 있습니다.`
}

/**
 * 홈 자연어 description 생성
 * @returns {string}
 */
export function buildHomeDescription () {
  return '다이소몰은 생활용품, 주방, 인테리어, 문구, 뷰티, 식품 등 다양한 카테고리의 상품을 합리적인 가격에 제공하는 온라인 쇼핑몰입니다. 균일가 생활용품 전문점 다이소의 공식 온라인몰에서 쇼핑하세요.'
}

/**
 * Product JSON-LD 스키마 생성
 * @param {Object} params
 * @param {string} params.name - 상품명
 * @param {string} params.image - 이미지 URL
 * @param {string} params.description - 상품 설명
 * @param {string} params.sku - 상품번호
 * @param {string} params.brand - 브랜드명
 * @param {string} params.url - canonical URL
 * @param {number|string} params.price - 가격
 * @param {string} params.availability - 재고 상태 ('InStock' | 'OutOfStock')
 * @param {string} [params.category] - 카테고리명
 * @param {Object} [params.rating] - 리뷰 평점 { avg, count }
 * @returns {Object}
 */
export function buildProductJsonLd ({ name, image, description, sku, brand, url, price, availability, category, rating }) {
  const product = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name,
    image: image || FALLBACK_IMAGE,
    description,
    sku,
    brand: { '@type': 'Brand', name: brand || '다이소' },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'KRW',
      price,
      itemCondition: 'https://schema.org/NewCondition',
      availability: `https://schema.org/${availability || 'OutOfStock'}`
    }
  }
  if (category) { product.category = category }
  if (rating && rating.count > 0) {
    product.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.avg,
      reviewCount: rating.count
    }
  }
  return product
}

/**
 * BreadcrumbList JSON-LD 스키마 생성
 * @param {Array<{name: string, url: string}>} items
 * @returns {Object}
 */
export function buildBreadcrumbJsonLd (items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url
    }))
  }
}

/**
 * SearchResultsPage JSON-LD 스키마 생성
 * @param {Object} params
 * @param {string} params.searchTerm - 검색어
 * @param {string} params.url - 페이지 URL
 * @returns {Object}
 */
export function buildSearchResultsJsonLd ({ searchTerm, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    name: `${searchTerm} 검색결과 - 다이소몰`,
    url,
    mainEntity: {
      '@type': 'WebSite',
      name: '다이소몰',
      url: BASE_URL
    }
  }
}

/**
 * FAQPage JSON-LD 스키마 생성
 * @param {Array<{question: string, answer: string}>} items - FAQ 항목 배열
 * @returns {Object}
 */
export function buildFAQPageJsonLd (items) {
  if (!items || items.length === 0) { return null }
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
}

export { FALLBACK_IMAGE, BASE_URL }
